import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const DiscountedCashFlow = () => {
  // State variables for Discounted Cash Flow (DCF) calculation
  const [cashFlow, setCashFlow] = useState(0); // Cash flow
  const [discountRate, setDiscountRate] = useState(0); // Discount rate
  const [numberOfPeriods, setNumberOfPeriods] = useState(0); // Number of periods
  const [dcf, setDcf] = useState(0); // Calculated discounted cash flow

  // Event handlers to update state values
  const handleCashFlowChange = (e) =>
    setCashFlow(parseFloat(e.target.value) || 0);
  const handleDiscountRateChange = (e) =>
    setDiscountRate(parseFloat(e.target.value) || 0);
  const handlePeriodsChange = (e) =>
    setNumberOfPeriods(parseInt(e.target.value) || 0);

  // useEffect hook to calculate Discounted Cash Flow (DCF)
  useEffect(() => {
    if (cashFlow > 0 && discountRate > 0 && numberOfPeriods > 0) {
      // DCF = Cash Flow / (1 + Discount Rate)^Number of Periods
      const discountedValue =
        cashFlow / Math.pow(1 + discountRate / 100, numberOfPeriods);
      setDcf(discountedValue);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [cashFlow, discountRate, numberOfPeriods]);

  return (
    <Layout title="Discounted Cash Flow Calculator" path="discounted-cash-flow">
      <section className="section-padding" id="discounted-cash-flow">
        <div className="container mt-5">
          <div className="row">
            {/* DCF Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Discounted Cash Flow Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Cash Flow */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="cashFlow" className="form-label">
                        Cash Flow
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Cash Flow:</strong> The net amount of cash being transferred into and out of a business, typically over a period of time."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="cashFlow"
                        placeholder="Enter Cash Flow"
                        value={cashFlow || ""}
                        onChange={handleCashFlowChange}
                      />
                    </div>

                    {/* Input for Discount Rate */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="discountRate" className="form-label">
                        Discount Rate (%)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Discount Rate:</strong> The interest rate used to discount future cash flows to the present value. It reflects the risk or opportunity cost of capital."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="discountRate"
                        placeholder="Enter Discount Rate"
                        value={discountRate || ""}
                        onChange={handleDiscountRateChange}
                      />
                    </div>

                    {/* Input for Number of Periods */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="numberOfPeriods" className="form-label">
                        Number of Periods
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Number of Periods:</strong> The number of time periods (usually years) over which the future cash flows will be discounted."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="numberOfPeriods"
                        placeholder="Enter Number of Periods"
                        value={numberOfPeriods || ""}
                        onChange={handlePeriodsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Discounted Cash Flow (DCF):{" "}
                        <span className="fw-bold ms-3">
                          {dcf === 0
                            ? "0"
                            : dcf.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Discounted Cash Flow (DCF)
                </h3>

                {/* What is DCF? */}
                <h4 className="mt-3 mb-3">
                  What is Discounted Cash Flow (DCF)?
                </h4>
                <p>
                  The **Discounted Cash Flow (DCF)** method is used to estimate
                  the value of an investment based on its expected future cash
                  flows. By applying a discount rate, it adjusts the future cash
                  flows to reflect their present value.
                </p>

                {/* Why Use DCF? */}
                <h4 className="mt-4 mb-3">
                  Why Use Discounted Cash Flow (DCF)?
                </h4>
                <p>
                  DCF is a fundamental tool in financial analysis that helps
                  investors and financial analysts assess the potential
                  profitability of an investment. It allows for more accurate
                  decision-making by accounting for the time value of money.
                </p>

                {/* Benefits of Using DCF */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using Discounted Cash Flow
                </h4>
                <ul>
                  <li>
                    <strong>Helps Evaluate Project Profitability:</strong>{" "}
                    Assists in determining whether an investment will provide
                    returns that exceed its cost.
                  </li>
                  <li>
                    <strong>
                      Supports Capital Budgeting and Investment Decisions:
                    </strong>{" "}
                    Aids in selecting projects or investments that align with
                    financial goals.
                  </li>
                  <li>
                    <strong>Provides Insight into Project Viability:</strong>{" "}
                    Helps gauge the long-term value and risks associated with
                    investments.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use DCF?</h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Financial Analysts:</strong> Supports financial
                    analysis and valuation of investment opportunities.
                  </li>
                  <li>
                    <strong>Investors:</strong> Provides valuable insights into
                    potential returns and risks before making investment
                    decisions.
                  </li>
                  <li>
                    <strong>Corporate Finance:</strong> Helps in capital
                    budgeting and evaluating project viability in corporate
                    settings.
                  </li>
                </ul>

                {/* Two Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How do I choose the right discount rate for DCF?
                </h4>
                <p>
                  The discount rate typically reflects the cost of capital,
                  which could include factors like risk, inflation, and the
                  opportunity cost of capital. It is often based on the
                  company’s weighted average cost of capital (WACC) or a
                  required rate of return.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is it important to consider the time value of money in DCF
                  analysis?
                </h4>
                <p>
                  The time value of money is important because it acknowledges
                  that money received today is worth more than the same amount
                  in the future due to its earning potential. DCF accounts for
                  this by discounting future cash flows to their present value,
                  ensuring a more accurate valuation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DiscountedCashFlow;
